/**
 * Change Password Page
 * Self-service password change (PATCH /users/me/password).
 *
 * Also the forced gate for users holding a temporary password: ProtectedRoute
 * sends any user with mustChangePassword here and keeps them off every other
 * route, and the API client redirects here on a 403 MUST_CHANGE_PASSWORD. The
 * page therefore renders outside AppLayout (whose nav would only lead to
 * blocked pages) and offers sign-out so a flagged user is never trapped.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAPIClient, IUsersApi } from '../api';
import { getErrorMessage } from '../utils/apiError';
import { getPasswordStrengthError, PASSWORD_REQUIREMENTS_HINT } from '../utils/password';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { Toast } from '../components/Toast';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, logout, markPasswordChanged } = useAuth();
  const apiClient = React.useMemo<IUsersApi>(() => createAPIClient(), []);
  const isForced = user?.mustChangePassword === true;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword) {
      setFormError('Enter your current password');
      return;
    }
    const strengthError = getPasswordStrengthError(newPassword);
    if (strengthError) {
      setFormError(strengthError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }
    if (newPassword === currentPassword) {
      setFormError('New password must be different from the current password');
      return;
    }

    try {
      setIsSaving(true);
      setFormError(null);
      await apiClient.changeOwnPassword(currentPassword, newPassword);
      markPasswordChanged();
      navigate('/', { replace: true });
    } catch (err) {
      // e.g. 400 INVALID_CURRENT_PASSWORD, 409 PASSWORD_CHANGED_CONCURRENTLY,
      // or a password-strength validation message.
      setFormError(getErrorMessage(err, 'Failed to change password'));
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit} noValidate>
          <div className="p-4 sm:p-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {isForced ? 'Set a new password' : 'Change password'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isForced
                ? 'Your password was set by an administrator. Choose a new password to continue.'
                : 'Update the password you use to sign in.'}
            </p>
            {user?.email && (
              <p className="mt-1 text-sm text-gray-600 break-words">
                Signed in as <strong>{user.email}</strong>
              </p>
            )}

            {formError && <Toast kind="error">{formError}</Toast>}

            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="currentPassword" className={labelClass}>
                  {isForced ? 'Temporary password' : 'Current password'}
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isSaving}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className={labelClass}>
                  New password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isSaving}
                  aria-describedby="newPasswordHint"
                  className={inputClass}
                />
                <p id="newPasswordHint" className="mt-1.5 text-xs text-gray-500">
                  {PASSWORD_REQUIREMENTS_HINT}
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className={labelClass}>
                  Confirm new password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSaving}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
            {isForced ? (
              <button type="button" onClick={handleLogout} disabled={isSaving} className={`${secondaryButtonClass} flex-1 sm:flex-none min-h-[44px] sm:min-h-0`}>
                Sign out
              </button>
            ) : (
              <Link to="/" className={`${secondaryButtonClass} inline-flex flex-1 sm:flex-none items-center justify-center min-h-[44px] sm:min-h-0`}>
                Cancel
              </Link>
            )}
            <button type="submit" disabled={isSaving} className={`${primaryButtonClass} flex-1 sm:flex-none min-h-[44px] sm:min-h-0`}>
              {isSaving ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
